import { Head, router } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { Stat } from "@/components/ui/stat"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table"
import { TrendChart } from "@/components/charts/TrendChart"
import { Funnel, type FunnelStep } from "@/components/charts/Funnel"
import { cn } from "@/lib/utils"

type Summary = {
  views: number
  uniques: number
  leads: number
  conversion_rate: number
}

type TrendPoint = { date: string; views: number; uniques: number }
type Source = {
  source: string
  views: number
  submits: number
  conversion_rate: number
}

type Props = {
  range: number
  ranges: number[]
  summary: Summary
  trend: TrendPoint[]
  funnel: FunnelStep[]
  sources: Source[]
}

const DESCRIPTION =
  "Private first-party analytics for the landing page: visitors, the conversion funnel, traffic sources, and leads."

export default function AdminDashboard({
  range,
  ranges,
  summary,
  trend,
  funnel,
  sources,
}: Props) {
  return (
    <>
      <Head title="Dashboard">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Admin dashboard" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AdminShell wide>
        <PageHeader
          title="Dashboard"
          description={`First-party analytics · last ${range} days`}
          actions={<RangeControl current={range} ranges={ranges} />}
        />

        <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Stat label="Views" value={formatNumber(summary.views)} />
          <Stat label="Unique visitors" value={formatNumber(summary.uniques)} />
          <Stat label="Leads" value={formatNumber(summary.leads)} />
          <Stat
            label="Conversion"
            value={`${summary.conversion_rate}%`}
            sub="leads / unique visitor"
          />
        </div>

        <section className="mt-8">
          <h2 className="mb-3">Visitors</h2>
          <TrendChart data={trend} />
        </section>

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-2">
          <section>
            <h2 className="mb-3">Conversion funnel</h2>
            <Funnel steps={funnel} />
          </section>

          <section>
            <h2 className="mb-3">Traffic sources</h2>
            {sources.length === 0 ? (
              <div className="flex h-32 items-center justify-center rounded-lg border border-hairline bg-page">
                <p className="text-sm text-ink-muted">No traffic sources in this range yet.</p>
              </div>
            ) : (
              <div className="overflow-hidden rounded-lg border border-hairline bg-page">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Source</TableHeaderCell>
                      <TableHeaderCell className="text-right">Views</TableHeaderCell>
                      <TableHeaderCell className="text-right">Submits</TableHeaderCell>
                      <TableHeaderCell className="text-right">Conv.</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sources.map((s) => (
                      <TableRow key={s.source}>
                        <TableCell className="font-medium text-ink-display">
                          {s.source}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatNumber(s.views)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatNumber(s.submits)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {s.conversion_rate}%
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </section>
        </div>
      </AdminShell>
    </>
  )
}

function RangeControl({ current, ranges }: { current: number; ranges: number[] }) {
  return (
    <div className="inline-flex rounded-md border border-hairline p-0.5">
      {ranges.map((r) => (
        <button
          key={r}
          type="button"
          onClick={() =>
            router.get("/admin", { range: r }, { preserveScroll: true, preserveState: false })
          }
          className={cn(
            "cursor-pointer rounded px-3 py-1 text-sm transition-colors",
            r === current
              ? "bg-accent-faded font-medium text-accent-display"
              : "text-ink-muted hover:text-ink-display",
          )}
          aria-pressed={r === current}
        >
          {r}d
        </button>
      ))}
    </div>
  )
}

function formatNumber(n: number) {
  return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
}
